import { LogLevelDesc } from "loglevel";

import bvvmOfficeList from "./assets/data/bvvm-office-list.json";
import version from "./version.json";

const env = { ...window.env };

const testEnvs = ["test", "local"];

const getBoolean = (value: string): boolean => {
    if (testEnvs.includes(process.env.NODE_ENV)) {
        return true;
    }

    return value === "true";
};

const getCfgValue = (value: string, defaultValue: string): string => {
    if (!!value && /^#\{.*}#$/.test(value)) {
        return defaultValue;
    }
    return value;
};

export type Config = {
    BUILD: {
        SOURCE: string;
        VERSION: string;
        BUILD_DATE: string;
        COMMIT_HASH: string;
    };
    NODE_ENV: string;
    PUBLIC_URL: string;
    ENV: string;
    IS_PROD: boolean;
    LOG_LEVEL: LogLevelDesc;
    ENVIRONMENT_COLOR: string;
    SPAPI_URL: string;
    IDP_URL: string;
    GOOGLE_TAGMANAGER_ID: string;
    GOOGLE_TAGMANAGER_AUTH: string;
    GOOGLE_TAGMANAGER_PREVIEW: string;
    ROLLBAR_ACCESS_TOKEN: string;
    MAPS_API_KEY: string;
    REFINER_TOKEN: string;
    REFINER_SURVEY_NL: string;
    REFINER_SURVEY_FR: string;
    REFINER_SURVEY_EN: string;
    REFINER_SURVEY_DE: string;
    REFINER_FEEDBACK_COUNT: number;
    FEATURES: {
        useGoogleMapsApi: boolean;
    };
    AVAILABLE_LANGUAGES: string[];
    FALLBACK_LANGUAGE: string;
    DEFAULT_PAGE_SIZE: number;
    DEFAULT_SEARCH_PAGE_SIZE: number;
    DEFAULT_LIST_SIZE: number;
    DEFAULT_CARD_LIST_SIZE: number;
    KEY_LIST_SIZE: string;
    KEY_CARD_LIST_SIZE: string;
    REQUEST_TIMEOUT: number;
    DATA_PROTECTION_AUTHORITY_URL: string;
    DATA_PROTECTION_AUTHORITY_EMAIL: string;
    HELP_PHONE_NUMBER: string;
    ADOBE_EMBED_API: string;
    OFFICE_IDS_FILTER: string[];
    ADOBE_EMBED_WWW_API: string;
    BVMM_OFFICE_LIST: any;
    MOBILE_APPS: {
        MYBROKER_IOS_URL: string;
        MYBROKER_ANDROID_URL: string;
    };
    APPINSIGHTS: {
        INSTRUMENTATIONKEY: string;
    };
    AXA_CLAIM_JUMP?: string;
};

const config: Config = {
    // BUILD INFO
    BUILD: {
        SOURCE: version.sourceBranch,
        VERSION: version.semanticVersion,
        BUILD_DATE: version.buildDate,
        COMMIT_HASH: version.commitHash,
    },

    // CRA
    NODE_ENV: process.env.NODE_ENV || "unknown",
    PUBLIC_URL: getCfgValue(env.PUBLIC_URL, "/"),

    // APP SPECIFIC
    ENV: getCfgValue(env.ENV, "local"),
    IS_PROD: ["prd", "prod"].includes(getCfgValue(env.ENV, "local")),
    LOG_LEVEL: getCfgValue(env.LOG_LEVEL, "debug") as LogLevelDesc,
    ENVIRONMENT_COLOR: getCfgValue(env.ENVIRONMENT_COLOR, "#eea6ff"),

    SPAPI_URL: getCfgValue(env.SPAPI_URL, "https://dev.mybroker.be"),
    IDP_URL: getCfgValue(env.IDP_URL, "https://exts-ibp-stgidp-cloud.portima.be/ibp/pi2/ip/itfs_s/sc2ip"),

    GOOGLE_TAGMANAGER_ID: getCfgValue(env.GOOGLE_TAGMANAGER_ID, ""),
    GOOGLE_TAGMANAGER_AUTH: getCfgValue(env.GOOGLE_TAGMANAGER_AUTH, ""),
    GOOGLE_TAGMANAGER_PREVIEW: getCfgValue(env.GOOGLE_TAGMANAGER_PREVIEW, ""),

    ROLLBAR_ACCESS_TOKEN: getCfgValue(env.ROLLBAR_ACCESS_TOKEN, ""),
    MAPS_API_KEY: getCfgValue(env.MAPS_API_KEY, ""),

    REFINER_TOKEN: getCfgValue(env.REFINER_TOKEN, ""),
    REFINER_SURVEY_NL: getCfgValue(env.REFINER_SURVEY_NL, ""),
    REFINER_SURVEY_FR: getCfgValue(env.REFINER_SURVEY_FR, ""),
    REFINER_SURVEY_EN: getCfgValue(env.REFINER_SURVEY_EN, ""),
    REFINER_SURVEY_DE: getCfgValue(env.REFINER_SURVEY_DE, ""),
    REFINER_FEEDBACK_COUNT: +getCfgValue(env.REFINER_FEEDBACK_COUNT, "0"),
    FEATURES: {
        useGoogleMapsApi: getBoolean(env.FEATURE_GOOGLE_MAPS_API),
    },

    AVAILABLE_LANGUAGES: ["en", "fr", "nl", "de"],
    FALLBACK_LANGUAGE: "en",

    DEFAULT_PAGE_SIZE: 256,
    DEFAULT_SEARCH_PAGE_SIZE: 256,
    DEFAULT_LIST_SIZE: 10,
    DEFAULT_CARD_LIST_SIZE: 5,

    KEY_LIST_SIZE: "listSize",
    KEY_CARD_LIST_SIZE: "cardListSize",

    REQUEST_TIMEOUT: +getCfgValue(env.REQUEST_TIMEOUT, "60"),

    DATA_PROTECTION_AUTHORITY_URL: "https://www.autoriteprotectiondonnees.be",
    DATA_PROTECTION_AUTHORITY_EMAIL: "contact@apd-gba.be",
    HELP_PHONE_NUMBER: "+3226614100",
    ADOBE_EMBED_API: getCfgValue(env.ADOBE_EMBED_API, "f3f7d4ea7e984f4d826d030ebf10d9a4"),
    OFFICE_IDS_FILTER: getCfgValue(env.OFFICE_IDS_FILTER || "", "150,151")
        .split(",")
        .filter((id) => !!id),
    ADOBE_EMBED_WWW_API: getCfgValue(env.ADOBE_EMBED_WWW_API, "f3f7d4ea7e984f4d826d030ebf10d9a4"),
    BVMM_OFFICE_LIST: bvvmOfficeList,
    MOBILE_APPS: {
        MYBROKER_IOS_URL: "https://apps.apple.com/be/app/mybroker-en/id517191352",
        MYBROKER_ANDROID_URL: "https://play.google.com/store/apps/details?id=com.portima.mybroker",
    },
    APPINSIGHTS: {
        INSTRUMENTATIONKEY: getCfgValue(env.APPINSIGHTS_INSTRUMENTATIONKEY, "b286f730-fe06-4dfa-9662-a9b3125f92db"),
    },
};

Object.defineProperty(config, "AXA_CLAIM_JUMP", {
    get: function () {
        return getCfgValue(env.AXA_CLAIM_JUMP, "https://www.fo.axa.be/claimsselfservicing/efnol/licensePlate");
    },
    set: function (value: string) {
        env.AXA_CLAIM_JUMP = value;
    },
    enumerable: true,
    configurable: true,
});

export default config;
